/**
 * Variant section — shows textual differences between
 * TR (KJV) and WH (critical text) manuscript traditions.
 * Details behind an accordion with impact and diff count summary.
 */

import { useState } from 'react';
import type { TextualVariant } from '@skjv/common';
import { ChevronRightIcon } from '@components/icons';
import { InlineStrongsChip } from './inline-strongs-chip';

interface VariantSectionProps {
  variants: TextualVariant[];
}

const CATEGORY_LABELS: Record<string, string> = {
  word_substitution: 'Word Substitution',
  addition: 'Addition',
  omission: 'Omission',
  major_addition: 'Major Addition',
  full_verse_omission: 'Full Verse Omission',
};

function countDifferences(variants: TextualVariant[]): number {
  let count = 0;
  for (const v of variants) {
    count += v.strongsOnlyInKJV.length + v.strongsOnlyInNASB.length;
  }
  return count;
}

function summarizeImpact(variants: TextualVariant[]): string {
  const impacts = variants.map((v) => v.theologicalImpact);
  if (impacts.includes('high')) return 'High';
  if (impacts.includes('medium')) return 'Medium';
  return 'Low';
}

export function VariantSection({ variants }: VariantSectionProps) {
  const [open, setOpen] = useState(false);
  if (variants.length === 0) return null;

  const diffCount = countDifferences(variants);
  const impact = summarizeImpact(variants);

  return (
    <div className="rich-message-section">
      <h4 className="rich-message-section-title">Textual Variants</h4>
      <button className="word-study-toggle" onClick={() => setOpen(!open)}>
        <ChevronRightIcon className={`h-3 w-3 word-study-chevron ${open ? 'word-study-chevron-open' : ''}`} />
        <span>{impact}. {diffCount} {diffCount === 1 ? 'difference' : 'differences'}.</span>
      </button>
      {open && (
        <>
        <p className="variant-explainer">
          Differences between the Textus Receptus (KJV) and Westcott-Hort (modern critical text) manuscripts.
        </p>
        <ul className="variant-list">
          {variants.map((v, i) => (
            <li key={`${v.reference}-${i}`} className="variant-item">
              <div className="variant-header">
                <span className="variant-category">
                  {CATEGORY_LABELS[v.category] ?? v.category}
                </span>
              </div>

              {v.strongsOnlyInKJV.length > 0 && (
                <p className="variant-detail">
                  <span className="variant-label">Only in KJV (TR):</span>{' '}
                  {v.strongsOnlyInKJV.map((s, j) => (
                    <span key={s}>
                      {j > 0 && ', '}
                      <InlineStrongsChip number={s} displayText={s} />
                    </span>
                  ))}
                </p>
              )}
              {v.strongsOnlyInNASB.length > 0 && (
                <p className="variant-detail">
                  <span className="variant-label">Only in NASB (WH):</span>{' '}
                  {v.strongsOnlyInNASB.map((s, j) => (
                    <span key={s}>
                      {j > 0 && ', '}
                      <InlineStrongsChip number={s} displayText={s} />
                    </span>
                  ))}
                </p>
              )}
              {v.affectedConcepts.length > 0 && (
                <p className="variant-concepts">
                  Affected concepts: {v.affectedConcepts.join(', ')}
                </p>
              )}
            </li>
          ))}
        </ul>
        </>
      )}
    </div>
  );
}
